import React, { useState, useEffect, Suspense } from "react";
import {useNavigate } from "react-router-dom";
import MainHeader from "./navigation/header/MainHeader";
import Sidebar from "./navigation/Sidebar";
import MainRoutes from "./MainRoutes";
import Grid from "@mui/material/Grid";
import DashboardLoadingController from "./controllers/DashboardLoadingController";
import { RemoveOnFullPage } from "./contexts/FullPageContext";
import { theme } from "./components/muiTheme"
import { useAuthStore } from "./contexts/AuthContext";
import LoadingContent from "./components/progress/LoadingContent";
import checkAccountValidity from "./helpers/getAccountStatus";
import {useSwitchRoleToMember} from "./apiCalls/users";
import { useBannerDispatch } from "./contexts/BannerContext";

const EditUsersModal = React.lazy(() => import("./modals/EditUsersModal"));
const UnpublishDashModal = React.lazy(() => import("./modals/UnpublishModal"));
const EmbeddedDashModal = React.lazy(() => import("./modals/EmbeddedDashModal"));
const Banner = React.lazy(() => import("./components/reusable-components/Banner"));
const MaintenanceCountdown = React.lazy(() => import("./components/reusable-components/MaintenanceCountdown"));
const EditorTrialCountdown = React.lazy(() => import("./components/reusable-components/EditorTrialCountdown"));
const DashboardAssignOwnershipModal = React.lazy(() => import("./modals/DashboardAssignOwnershipModal"));

function Main() {
  const { user, selectedAccount } = useAuthStore();
  const navigate = useNavigate();
  const [editorEndTime, setEditorEndTime] = useState(null);
  const [showCountDown, setShowCountDown] = useState(false);
  const { switchRoleToMember } = useSwitchRoleToMember(user.id, selectedAccount.id);
  const bannerDispatch = useBannerDispatch();

  useEffect(() => {
    if (user && user.termsAcceptedOn && selectedAccount) {
      const { isTrialValid, isSubscriptionValid } =
        checkAccountValidity(selectedAccount);

      // Determine if the countdown should be shown
      if(isSubscriptionValid){//if in a paid account, not show the countdown
        setShowCountDown(false);
      }else if(isTrialValid){
        //if in a trial account
        const termsAcceptedDate = new Date(user.termsAcceptedOn);
        // Calculate the date 30 days after termsAcceptedOn
        const endDate = new Date(termsAcceptedDate);
        endDate.setDate(termsAcceptedDate.getDate() + 30);

        //get the userrole
        const firstUserRoleEntry = user.UserRoles
          ? Object.values(user.UserRoles)[0]?.Role
          : {};
        const userRoleName = Object.values(firstUserRoleEntry || {})[0]?.name;

        const currentDate = new Date(); // Get the current date and time

        if (userRoleName === "Editor" && currentDate <= endDate) {
          setEditorEndTime(endDate); // Set the end time for the countdown
          setShowCountDown(true); // Show the countdown
        } else if (userRoleName === "Editor" && currentDate > endDate) {
          setEditorEndTime(null); // Clear the end time
          setShowCountDown(false); // Hide the countdown
          //also need to switch role to member
          handleEditorTrialEndFunc();
        } 
      }else{
        setShowCountDown(false);
      }
    }
  }, [user, selectedAccount]);

  useEffect(() => {
    if(!user.viewContentStartOn){
      if(user.isSuperAdmin){
        navigate(`/workspace/${selectedAccount.uniqueHash}`)
      }else{
        navigate(
          `/marketplace/${selectedAccount.uniqueHash}/dashboard/JXZHJDwXZp` //for the first time login, we can change it in future as needed
        );
      }
    }else{
      const storeUrl = localStorage.getItem("storedUrl");
      if (storeUrl) {
        const redirectPath = storeUrl.split("/").slice(3).join("/");
        const pathWay = window.location.href.split("/").slice(3).join("/");
        redirectPath ? navigate(redirectPath) :
          navigate(pathWay);
      }
    }
    // Use setTimeout to ensure localStorage is updated after navigation
    setTimeout(() => {
      localStorage.setItem("storedUrl", ""); // Set storeUrl to ""
    }, 0);
  }, [])

  //after the editor trial end, update the editor role to member role
  const handleEditorTrialEndFunc = async() => {
    const updatedUser = await switchRoleToMember();
    if (!updatedUser) {
      bannerDispatch({
        type: "changeBannerState",
        payload: {
          mainText: "Failed to switch your role.",
          subText: "",
          tallBanner: false,
          success: false,
        },
      });
    }
    navigate(`/workspace/${selectedAccount.uniqueHash}`, { replace: true });
    window.location.reload(); //refresh the page after navigation
  }

  return (
    <Grid
      container
      direction="row"
      className="inno-main-container"
      sx={{
        height: "100%",
        minHeight: "630px",
        maxHeight: "100%",
        minWidth: theme.main.sidebarWidth + theme.main.headerMinWidth,
      }}>
      {/* Independent Controller for loading dashboards, does not render anything */}
      <DashboardLoadingController />

      <RemoveOnFullPage>
        <Grid item height="100%">
          <Sidebar />
        </Grid>
      </RemoveOnFullPage>
      <Grid
        item
        //flexGrow allows this to fill the space. The width serves as a minimum
        flexGrow={1}
        minWidth={theme.main.headerMinWidth}
        width="500px"
        maxWidth="100%"
        container
        direction="column"
        justifyContent="space-between"
        sx={{
          height: "100%",
        }}>
        <RemoveOnFullPage>
          <Grid item sx={{ height: theme.main.headerHeight, width: "100%" }}>
            <MainHeader />
          </Grid>
        </RemoveOnFullPage>
        <Grid
          item
          flexGrow={1}
          height="50px"
          minHeight="500px"
          width="100%"
          padding="0px 30px 0px 0px">
          <MainRoutes />
        </Grid>
      </Grid>

      {/* Modals here are ones that may potentially be called from various places in the app */}
      <Suspense fallback={<LoadingContent />}>
        <EditUsersModal />
        <DashboardAssignOwnershipModal />
        <UnpublishDashModal />
        <EmbeddedDashModal />{/*move this modal here to make sure that we can open it on any page, for example on a dash view page*/}
        <Banner />
        <MaintenanceCountdown />
        {showCountDown && (
          <EditorTrialCountdown
            scheduledTime={editorEndTime}
            handleEndFunc={handleEditorTrialEndFunc}
          />
        )}
      </Suspense>
    </Grid>
  );
}

export default Main;
